import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft,
    Package,
    CalendarDays,
    CreditCard,
    IndianRupee,
    User,
    RefreshCcw,
    Box,
    Loader2,
    Phone,
    Mail,
    Bike,
    Users,
    Info,
    ShieldCheck,
    Clock3,
    FileCheck,
    Smartphone,
    Package2,
    ListOrdered,
    CheckCircle2,
    BadgeDollarSign,
    Truck,
    PackageCheck,
    BadgeInfo,
    XCircle,
    UserX,
    ShoppingBag,
} from "lucide-react";
import useNotification from "../../Sevices/useNotification"; // adjust path if needed

const statusClasses = {
    unassigned: "bg-gray-100 text-gray-700 border-gray-200",
    accepted: "bg-blue-100 text-blue-700 border-blue-200",
    paid: "bg-violet-100 text-violet-700 border-violet-200",
    "on the way": "bg-orange-100 text-orange-700 border-orange-200",
    delivered: "bg-green-100 text-green-700 border-green-200",
};

export default function DriverOrdersPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { driverId } = useParams();

    const driver = location.state?.driver || null;

    const {
        getDriverProfileDetails,
        getDriverAllOrders,
        updateOrderStatus,
        driverProfileLoading,
        orderDetailsLoading,
        orderStatusLoadingId,
    } = useNotification();

    const [orders, setOrders] = useState([]);
    const [loadingPage, setLoadingPage] = useState(true);
    const [driverProfile, setDriverProfile] = useState(null);
    const [showMoreInfo, setShowMoreInfo] = useState(false);
 const loadOrders = async () => {
  setLoadingPage(true);

  const [profile, orderData] = await Promise.all([
    getDriverProfileDetails(driverId),
    getDriverAllOrders({ driverId, orderId: "" }),
  ]);

  const normalized =
    Array.isArray(orderData) && orderData.length > 0
      ? orderData.flatMap((group) => group?.orderDetails || [])
      : [];

  setDriverProfile(profile || null);
  setOrders(normalized);
  setLoadingPage(false);
};
    useEffect(() => {
        if (driverId) {
            loadOrders();
        }
    }, [driverId]);

    const stats = useMemo(() => {
        const totalOrders = orders.length;
        const totalValue = orders.reduce(
            (sum, item) => sum + Number(item.price || 0),
            0
        );
        const codCount = orders.filter(
            (item) => (item.payment_mode || "").toUpperCase() === "COD"
        ).length;
        const deliveredCount = orders.filter(
            (item) => (item.status || "").toLowerCase() === "delivered"
        ).length;

        return {
            totalOrders,
            totalValue,
            codCount,
            deliveredCount,
        };
    }, [orders]);

    const formatDateTime = (value) => {
        if (!value) return "-";
        try {
            return new Date(value).toLocaleString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            });
        } catch {
            return value;
        }
    };

    const getStatusClass = (status) => {
        const key = (status || "").toLowerCase();
        return (
            statusClasses[key] || "bg-gray-100 text-gray-700 border-gray-200"
        );
    };

    const getItemCount = (orderDetails) =>
        Array.isArray(orderDetails)
            ? orderDetails.reduce((sum, item) => sum + Number(item.quantity || 0), 0)
            : 0;
const toggleDriverMoreInfo = () => {
  setShowMoreInfo((prev) => !prev);
};

const handleOrderStatusUpdate = async (orderId, status) => {
  await updateOrderStatus({
    orderId,
    status,
    onSuccess: async () => {
      const data = await getDriverAllOrders({
        driverId,
        orderId: "",
      });

      const flattenedOrders = (data || []).flatMap(
        (group) => group?.orderDetails || []
      );

      setOrders(flattenedOrders);
    },
  });
};
 return (
  <div className="min-h-screen p-1 md:p-6">
    <div className="mx-auto max-w-7xl space-y-5">
      {/* Header */}
      <div className="rounded-3xl border border-gray-200 bg-white p-4 shadow-sm md:p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-start gap-3">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-700 transition hover:bg-gray-50"
            >
              <ArrowLeft size={18} />
            </button>

            <div>
              <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-700">
                <ShoppingBag className="h-3.5 w-3.5" />
                Driver Orders
              </div>

              <h1 className="text-xl font-semibold text-gray-900">
                Driver Order Details
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Review driver profile, ordered items, and update order status
              </p>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-2 rounded-full bg-orange-50 px-3 py-1 text-xs font-medium text-orange-700">
                  <User size={13} />
                  {driver?.fullName || driver?.username || driverProfile?.username || driverId}
                </span>

                <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                  {driver?.driverId || driverId}
                </span>
              </div>
            </div>
          </div>

          <button
            onClick={loadOrders}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-orange-600"
          >
            <RefreshCcw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Total Orders
          </p>
          <p className="mt-2 text-2xl font-bold text-gray-900">
            {stats.totalOrders}
          </p>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Total Value
          </p>
          <p className="mt-2 flex items-center text-2xl font-bold text-gray-900">
            <IndianRupee size={20} />
            {stats.totalValue.toLocaleString("en-IN")}
          </p>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            COD Orders
          </p>
          <p className="mt-2 text-2xl font-bold text-gray-900">
            {stats.codCount}
          </p>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Delivered
          </p>
          <p className="mt-2 text-2xl font-bold text-gray-900">
            {stats.deliveredCount}
          </p>
        </div>
      </div>

      {/* Driver Summary */}
      <div className="overflow-hidden rounded-[28px] border border-orange-100 bg-white shadow-sm">
        <div className="border-b border-orange-100 bg-gradient-to-r from-orange-50 via-amber-50 to-white px-5 py-4">
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-orange-100">
              <User className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-900">
                Driver Summary
              </h4>
              <p className="text-xs text-gray-500">
                Driver who placed the order
              </p>
            </div>
          </div>
        </div>

        <div className="p-5">
          {driverProfileLoading ? (
            <div className="flex items-center justify-center gap-3 rounded-3xl border border-dashed border-orange-200 bg-orange-50/50 px-4 py-10 text-sm text-gray-600">
              <Loader2 className="h-5 w-5 animate-spin text-orange-500" />
              Loading driver details...
            </div>
          ) : driverProfile ? (
            <>
              <div className="flex flex-col gap-4 md:flex-row md:items-start">
                <div className="relative">
                  <img
                    src={driverProfile?.profileUrl || "/placeholder-user.png"}
                    alt={driverProfile?.username || "Driver"}
                    className="h-20 w-20 rounded-[22px] border border-orange-200 bg-orange-50 object-cover shadow-sm"
                  />
                  <div className="absolute -bottom-2 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full border border-white bg-white px-2 py-1 text-[10px] font-semibold text-gray-700 shadow">
                    <div
                      className={`h-2 w-2 rounded-full ${
                        String(driverProfile?.duty_status || "")
                          .toLowerCase()
                          .includes("on")
                          ? "bg-emerald-500"
                          : "bg-red-500"
                      }`}
                    />
                    {driverProfile?.duty_status || "—"}
                  </div>
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-lg font-bold text-gray-900">
                      {driverProfile?.username || "Driver"}
                    </h3>

                    <span className="rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-700">
                      {driverProfile?.driverCode || "—"}
                    </span>

                    {!!driverProfile?.kycStatus && (
                      <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold capitalize text-emerald-700">
                        {driverProfile?.kycStatus}
                      </span>
                    )}
                  </div>

                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    <div className="rounded-2xl border border-gray-100 bg-gray-50 p-3">
                      <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        <Phone className="h-3.5 w-3.5" />
                        Phone
                      </div>
                      <p className="truncate text-sm font-semibold text-gray-800">
                        {driverProfile?.phone || "—"}
                      </p>
                    </div>

                    <div className="rounded-2xl border border-gray-100 bg-gray-50 p-3">
                      <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        <Mail className="h-3.5 w-3.5" />
                        Email
                      </div>
                      <p className="truncate text-sm font-semibold text-gray-800">
                        {driverProfile?.email || "—"}
                      </p>
                    </div>

                    <div className="rounded-2xl border border-gray-100 bg-gray-50 p-3">
                      <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        <Bike className="h-3.5 w-3.5" />
                        Vehicle
                      </div>
                      <p className="truncate text-sm font-semibold text-gray-800">
                        {driverProfile?.transportType || "—"}
                      </p>
                    </div>

                    <div className="rounded-2xl border border-gray-100 bg-gray-50 p-3">
                      <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        <Users className="h-3.5 w-3.5" />
                        Active Team
                      </div>
                      <p className="truncate text-sm font-semibold text-gray-800">
                        {driverProfile?.activeTeamName || "—"}
                      </p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={toggleDriverMoreInfo}
                  className="inline-flex shrink-0 items-center gap-2 self-start rounded-2xl border border-orange-200 bg-orange-50 px-4 py-2 text-sm font-semibold text-orange-700 transition-all hover:bg-orange-100"
                >
                  <Info className="h-4 w-4" />
                  {showMoreInfo ? "Hide Info" : "More Info"}
                </button>
              </div>

              {showMoreInfo && (
                <div className="mt-5 grid grid-cols-1 gap-3 border-t border-orange-100 pt-5 sm:grid-cols-2 xl:grid-cols-3">
                  <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                    <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <ShieldCheck className="h-4 w-4" />
                      Primary Team
                    </div>
                    <p className="text-sm font-semibold text-gray-800">
                      {driverProfile?.primaryTeamName || "—"}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                    <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <Clock3 className="h-4 w-4" />
                      Shift End
                    </div>
                    <p className="text-sm font-semibold text-gray-800">
                      {driverProfile?.shift_end || "—"}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                    <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <BadgeInfo className="h-4 w-4" />
                      Licence Plate
                    </div>
                    <p className="text-sm font-semibold text-gray-800">
                      {driverProfile?.licencePlate || "—"}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                    <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <CalendarDays className="h-4 w-4" />
                      Joining Date
                    </div>
                    <p className="text-sm font-semibold text-gray-800">
                      {driverProfile?.joiningDate
                        ? formatDateTime(driverProfile.joiningDate)
                        : "—"}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                    <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <FileCheck className="h-4 w-4" />
                      KYC Step
                    </div>
                    <p className="text-sm font-semibold text-gray-800">
                      {driverProfile?.kycStep || "—"}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                    <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <Smartphone className="h-4 w-4" />
                      Device ID
                    </div>
                    <p className="truncate text-sm font-semibold text-gray-800">
                      {driverProfile?.deviceId || "—"}
                    </p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="rounded-3xl border border-dashed border-gray-200 bg-gray-50 px-4 py-10 text-center">
              <UserX className="mx-auto mb-3 h-10 w-10 text-gray-300" />
              <p className="text-sm font-medium text-gray-600">
                Driver details not found
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Orders Section */}
      <div className="overflow-hidden rounded-[28px] border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 px-5 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-100">
                <Package2 className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-900">
                  Order Records
                </h4>
                <p className="text-xs text-gray-500">
                  Showing all available driver orders
                </p>
              </div>
            </div>

            <div className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-semibold text-gray-600">
              {orders?.length || 0} Order{(orders?.length || 0) > 1 ? "s" : ""}
            </div>
          </div>
        </div>

        <div className="p-5">
          {loadingPage || orderDetailsLoading ? (
            <div className="flex items-center justify-center gap-3 rounded-3xl border border-dashed border-orange-200 bg-orange-50/40 px-4 py-14 text-sm text-gray-600">
              <Loader2 className="h-5 w-5 animate-spin text-orange-500" />
              Loading order details...
            </div>
          ) : orders?.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-gray-200 bg-gray-50 px-4 py-14 text-center">
              <ShoppingBag className="mx-auto mb-3 h-12 w-12 text-gray-300" />
              <h4 className="text-base font-semibold text-gray-800">
                No orders found
              </h4>
              <p className="mt-1 text-sm text-gray-500">
                There are no order details available for this driver.
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              {orders.reverse().map((order) => (
                <div
                  key={order?.pk_order_id}
                  className="overflow-hidden rounded-[26px] border border-gray-200 bg-gradient-to-b from-white to-gray-50 shadow-sm"
                >
                  {/* Order Head */}
                  <div className="border-b border-gray-100 bg-white px-5 py-4">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h4 className="text-lg font-bold text-gray-900">
                            Order #{order?.pk_order_id || "—"}
                          </h4>

                          {order?.payment_mode && (
                            <span className="rounded-full border border-violet-200 bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700">
                              {order.payment_mode}
                            </span>
                          )}

                          {order?.status && (
                            <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold capitalize text-blue-700">
                              {order.status}
                            </span>
                          )}
                        </div>

                        <p className="mt-1 text-sm text-gray-500">
                          Created{" "}
                          {order?.created_on ? formatDateTime(order.created_on) : "—"}
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                        <div className="rounded-2xl border border-gray-100 bg-orange-50 p-3 text-center">
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-orange-700">
                            Amount
                          </p>
                          <p className="mt-1 text-sm font-bold text-gray-900">
                            ₹ {Number(order?.price || 0).toFixed(2)}
                          </p>
                        </div>

                        <div className="rounded-2xl border border-gray-100 bg-gray-50 p-3 text-center">
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                            Items
                          </p>
                          <p className="mt-1 text-sm font-bold text-gray-900">
                            {order?.order_details?.length || 0}
                          </p>
                        </div>

                        <div className="rounded-2xl border border-gray-100 bg-gray-50 p-3 text-center">
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                            Payment
                          </p>
                          <p className="mt-1 text-sm font-bold text-gray-900">
                            {order?.payment_mode || "—"}
                          </p>
                        </div>

                        <div className="rounded-2xl border border-gray-100 bg-gray-50 p-3 text-center">
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                            Status
                          </p>
                          <p className="mt-1 text-sm font-bold capitalize text-gray-900">
                            {order?.status || "—"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Order Items */}
                  <div className="px-5 py-5">
                    <div className="mb-3 flex items-center gap-2">
                      <ListOrdered className="h-4 w-4 text-gray-500" />
                      <p className="text-sm font-semibold text-gray-800">
                        Ordered Items
                      </p>
                    </div>

                    <div className="space-y-3">
                      {(order?.order_details || []).map((product, idx) => (
                        <div
                          key={`${order?.pk_order_id}-${product?.itemId}-${idx}`}
                          className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-white p-3 shadow-sm"
                        >
                          <div className="h-16 w-16 shrink-0 overflow-hidden rounded-2xl border border-gray-200 bg-gray-50">
                            <img
                              src={product?.itemImage || "https://via.placeholder.com/70x70?text=Item"}
                              alt={product?.itemName || "Item"}
                              className="h-full w-full object-cover"
                              onError={(e) => {
                                e.currentTarget.src = "https://via.placeholder.com/70x70?text=Item";
                              }}
                            />
                          </div>

                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold text-gray-900">
                              {product?.itemName || "Item"}
                            </p>

                            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                              <span className="rounded-full bg-gray-100 px-2 py-1 font-medium">
                                Item ID: {product?.itemId || "—"}
                              </span>
                              <span className="rounded-full bg-orange-100 px-2 py-1 font-medium text-orange-700">
                                Qty: {product?.quantity || "0"}
                              </span>
                            </div>
                          </div>

                          <div className="hidden h-10 w-10 items-center justify-center rounded-xl bg-orange-50 sm:flex">
                            <Package className="h-5 w-5 text-orange-600" />
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Bottom Actions */}
                    <div className="mt-5 border-t border-gray-100 pt-5">
                      <div className="mb-3 flex items-center gap-2">
                        <RefreshCcw className="h-4 w-4 text-orange-600" />
                        <p className="text-sm font-semibold text-gray-800">
                          Update Order Status
                        </p>
                      </div>

                      <div className="flex flex-wrap gap-2.5">
                        {[
                          {
                            label: "Accepted",
                            icon: CheckCircle2,
                            cls: "bg-emerald-600 text-white hover:bg-emerald-700",
                          },
                          {
                            label: "Rejected",
                            icon: XCircle,
                            cls: "bg-red-600 text-white hover:bg-red-700",
                          },
                          {
                            label: "Paid",
                            icon: BadgeDollarSign,
                            cls: "bg-violet-600 text-white hover:bg-violet-700",
                          },
                          {
                            label: "On The Way",
                            icon: Truck,
                            cls: "bg-orange-500 text-white hover:bg-orange-600",
                          },
                          {
                            label: "Delivered",
                            icon: PackageCheck,
                            cls: "bg-blue-600 text-white hover:bg-blue-700",
                          },
                        ].map((action) => {
                          const Icon = action.icon;
                          const isUpdating = orderStatusLoadingId === order?.pk_order_id;

                          return (
                            <button
                              key={action.label}
                              onClick={() =>
                                handleOrderStatusUpdate(order?.pk_order_id, action.label)
                              }
                              disabled={isUpdating}
                              className={`inline-flex items-center gap-2 rounded border px-4 py-2.5 text-sm font-semibold shadow-sm transition-all disabled:cursor-not-allowed disabled:opacity-60 ${action.cls}`}
                            >
                              {isUpdating ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Icon className="h-4 w-4" />
                              )}
                              {action.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);
}